import { apiClient } from "@/shared/services/api";
import { randomId } from "@/shared/utils/randomId";

interface ApiResponse<T> {
  success: boolean;
  message: string | null;
  errorCode: string | null;
  errors: Record<string, string[]> | null;
  data: T;
}

const unwrap = <T>(payload: ApiResponse<T> | T): T => {
  if (
    typeof payload === "object" &&
    payload !== null &&
    "data" in (payload as ApiResponse<T>) &&
    "success" in (payload as ApiResponse<T>)
  ) {
    return (payload as ApiResponse<T>).data;
  }
  return payload as T;
};

// ── Types mirror the backend Common.DTOs.PointsDtos ─────────────────────────

export type PointsEntryType = "Earned" | "Redeemed" | "ManualAdjustment" | "Reversal";
export type PointsEntryStatus = "Pending" | "Approved" | "Rejected" | "Reversed";

export interface PointsAccount {
  id: string;
  customerId: string;
  customerName: string;
  isActive: boolean;
  pointsBalance: number;
  pendingPoints: number;
  lastComputedAt?: string | null;
}

export interface PointsLedgerEntry {
  id: string;
  customerPointsAccountId: string;
  customerId: string;
  customerName: string;
  direction: "Credit" | "Debit";
  type: PointsEntryType;
  points: number;
  status: PointsEntryStatus;
  orderId?: string | null;
  orderNumber?: string | null;
  paymentId?: string | null;
  pointsConfigurationId?: string | null;
  reason?: string | null;
  reference?: string | null;
  approvedByUserId?: string | null;
  approvedAt?: string | null;
  approvalNotes?: string | null;
  rejectionReason?: string | null;
  reversesEntryId?: string | null;
  createdAt: string;
  createdByUserId?: string | null;
  cause?: string | null;
}

export interface RedeemablePointsPreview {
  pointsBalance: number;
  minRedeemablePoints: number;
  maxRedeemablePointsPerOrder?: number | null;
  pointValueKes: number;
  maxRedeemablePoints: number;
  maxRedeemableKes: number;
}

export interface EarningPreview {
  hasActiveConfig: boolean;
  qualifies: boolean;
  points: number;
  configName?: string | null;
}

export interface EarningPreviewParams {
  orderAmount: number;
  orderType?: string | null;
  productCategories?: string[];
}

export interface CreatePointsAdjustmentRequest {
  customerId: string;
  points: number;
  direction: "Credit" | "Debit";
  reason: string;
  reference?: string | null;
}

export interface RedeemPointsRequest {
  customerId: string;
  points: number;
  reference?: string | null;
}

export interface ApprovePointsEntryRequest {
  approvalNotes?: string | null;
}

export interface RejectPointsEntryRequest {
  rejectionReason: string;
}

export interface ReversePointsEntryRequest {
  reason: string;
}

// ── Helpers ─────────────────────────────────────────────────────────────────

const idempotencyHeader = (key: string) => ({ headers: { "Idempotency-Key": key } });
export const generateIdempotencyKey = (): string => randomId();

// ── API surface ──────────────────────────────────────────────────────────────

export const pointsApi = {
  getByCustomer: async (customerId: string): Promise<PointsAccount> => {
    const { data } = await apiClient.get<ApiResponse<PointsAccount> | PointsAccount>(
      `/points/customers/${customerId}`
    );
    return unwrap<PointsAccount>(data);
  },

  getStatement: async (
    customerId: string,
    params?: { from?: string; to?: string; pageNumber?: number; pageSize?: number }
  ): Promise<PointsLedgerEntry[]> => {
    const { data } = await apiClient.get<ApiResponse<PointsLedgerEntry[]> | PointsLedgerEntry[]>(
      `/points/customers/${customerId}/statement`,
      { params }
    );
    return unwrap<PointsLedgerEntry[]>(data);
  },

  getRedeemable: async (customerId: string): Promise<RedeemablePointsPreview> => {
    const { data } = await apiClient.get<ApiResponse<RedeemablePointsPreview> | RedeemablePointsPreview>(
      `/points/customers/${customerId}/redeemable`
    );
    return unwrap<RedeemablePointsPreview>(data);
  },

  getEarningPreview: async (customerId: string, params: EarningPreviewParams): Promise<EarningPreview> => {
    const { data } = await apiClient.get<ApiResponse<EarningPreview> | EarningPreview>(
      `/points/customers/${customerId}/earning-preview`,
      {
        params: {
          orderAmount: params.orderAmount,
          orderType: params.orderType ?? undefined,
          productCategories: params.productCategories,
        },
        // Repeated keys (productCategories=A&productCategories=B) so ASP.NET binds string[].
        paramsSerializer: { indexes: null },
      }
    );
    return unwrap<EarningPreview>(data);
  },

  getPending: async (params?: {
    type?: string;
    customer?: string;
    pageNumber?: number;
    pageSize?: number;
  }): Promise<PointsLedgerEntry[]> => {
    const { data } = await apiClient.get<ApiResponse<PointsLedgerEntry[]> | PointsLedgerEntry[]>(
      `/points/pending`,
      { params }
    );
    return unwrap<PointsLedgerEntry[]>(data);
  },

  createAdjustment: async (
    payload: CreatePointsAdjustmentRequest,
    idempotencyKey: string
  ): Promise<PointsLedgerEntry> => {
    const { data } = await apiClient.post<ApiResponse<PointsLedgerEntry> | PointsLedgerEntry>(
      `/points/adjustments`,
      payload,
      idempotencyHeader(idempotencyKey)
    );
    return unwrap<PointsLedgerEntry>(data);
  },

  redeem: async (payload: RedeemPointsRequest, idempotencyKey: string): Promise<PointsLedgerEntry> => {
    const { data } = await apiClient.post<ApiResponse<PointsLedgerEntry> | PointsLedgerEntry>(
      `/points/redeem`,
      payload,
      idempotencyHeader(idempotencyKey)
    );
    return unwrap<PointsLedgerEntry>(data);
  },

  approve: async (
    entryId: string,
    payload: ApprovePointsEntryRequest,
    idempotencyKey: string
  ): Promise<PointsLedgerEntry> => {
    const { data } = await apiClient.post<ApiResponse<PointsLedgerEntry> | PointsLedgerEntry>(
      `/points/entries/${entryId}/approve`,
      payload,
      idempotencyHeader(idempotencyKey)
    );
    return unwrap<PointsLedgerEntry>(data);
  },

  reject: async (
    entryId: string,
    payload: RejectPointsEntryRequest,
    idempotencyKey: string
  ): Promise<PointsLedgerEntry> => {
    const { data } = await apiClient.post<ApiResponse<PointsLedgerEntry> | PointsLedgerEntry>(
      `/points/entries/${entryId}/reject`,
      payload,
      idempotencyHeader(idempotencyKey)
    );
    return unwrap<PointsLedgerEntry>(data);
  },

  reverse: async (
    entryId: string,
    payload: ReversePointsEntryRequest,
    idempotencyKey: string
  ): Promise<PointsLedgerEntry> => {
    const { data } = await apiClient.post<ApiResponse<PointsLedgerEntry> | PointsLedgerEntry>(
      `/points/entries/${entryId}/reverse`,
      payload,
      idempotencyHeader(idempotencyKey)
    );
    return unwrap<PointsLedgerEntry>(data);
  },
};
